<script lang="ts">
  // Admin > Emails > All inboxes: every user's connected mailboxes plus a
  // per-user review queue, managed on their behalf. Reuses
  // EmailAccountConnect (scoped via adminEmailAccountsApi.forUser) for the
  // connect/edit/sync/disconnect UI and EmailReviewQueue (scoped via
  // adminEmailReviewApi/adminEmailBackfillApi.forUser) for the review-queue
  // actions — same components/markup the user's own Emails page uses, just
  // parameterized onto the /admin/... on-behalf-of-user endpoints instead of
  // /me/... (see internal/api/handlers/admin_email_accounts.go and
  // email_review.go's admin variants).
  import { createQuery } from '@tanstack/svelte-query';
  import { ChevronDown, ChevronRight, Mail } from 'lucide-svelte';
  import { adminUsersApi, adminEmailAccountsApi, adminEmailReviewApi, adminEmailBackfillApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import EmailAccountConnect from '$lib/components/EmailAccountConnect.svelte';
  import EmailReviewQueue from '$lib/components/entities/EmailReviewQueue.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { AdminUser } from '$lib/types';

  // ponytail: no pagination — same small-org audit-screen tradeoff as
  // EmailAccessPage (per_page 200 comfortably covers real headcount); if
  // that ever gets tight, paginate like every other admin list.
  const usersQuery = createQuery(() => ({
    queryKey: ['admin-users-all-inboxes'],
    queryFn: () => adminUsersApi.list({ per_page: 200 })
  }));
  const users = $derived(usersQuery.data?.results ?? []);

  // Which users' "manage mailboxes" panel is expanded — a plain expand/
  // collapse, not a route: this is a dense audit list, not a page per user.
  let expanded = $state<Set<number>>(new Set());
  function toggleExpanded(id: number) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    expanded = next;
  }

  let reviewQueueUser = $state<AdminUser | null>(null);

  function mailboxSummary(u: AdminUser): string {
    if (u.email_account_count === 0) return 'No mailbox connected';
    const label = u.email_account_count === 1 ? '1 mailbox connected' : `${u.email_account_count} mailboxes connected`;
    return u.email_account_status === 'error' ? `${label} — one has a connection error` : label;
  }
  function mailboxDotColor(u: AdminUser): string {
    if (u.email_account_status === 'connected') return '#97D700';
    if (u.email_account_status === 'error') return '#C0392B';
    return '#DBDBDB';
  }
</script>

<div>
  {#if usersQuery.isLoading}
    <p class="tym-loading-state">Loading&hellip;</p>
  {:else if usersQuery.isError}
    <p class="tym-error-state">{getErrorMessage(usersQuery.error)}</p>
  {:else}
    <div class="space-y-2.5">
      {#each users as u (u.id)}
        <div class="tym-card px-4 py-3.5">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              class="flex min-w-0 flex-1 items-center gap-2.5 border-0 bg-transparent p-0 text-left"
              onclick={() => toggleExpanded(u.id)}
            >
              {#if expanded.has(u.id)}
                <ChevronDown class="h-4 w-4 shrink-0 text-dark-gray" />
              {:else}
                <ChevronRight class="h-4 w-4 shrink-0 text-dark-gray" />
              {/if}
              <Mail class="h-4 w-4 shrink-0 text-toyama-teal" />
              <div class="min-w-0">
                <div class="truncate text-[13.5px] font-semibold text-navy">{u.name || u.email}</div>
                <div class="flex items-center gap-1.5 text-[11.5px] text-dark-gray">
                  <span class="h-2 w-2 shrink-0 rounded-full" style="background:{mailboxDotColor(u)}"></span>
                  {mailboxSummary(u)}
                </div>
              </div>
            </button>
            <button type="button" class="btn-secondary shrink-0" onclick={() => (reviewQueueUser = u)}>
              View review queue
            </button>
          </div>
          {#if expanded.has(u.id)}
            <div class="mt-3 border-t border-toyama-cloudy-gray pt-3">
              <EmailAccountConnect api={adminEmailAccountsApi.forUser(u.id)} queryKey={['admin-email-accounts', u.id]} />
            </div>
          {/if}
        </div>
      {:else}
        <p class="tym-empty-state">No users yet.</p>
      {/each}
    </div>
  {/if}
</div>

{#if reviewQueueUser}
  <Modal title={`Review queue — ${reviewQueueUser.name || reviewQueueUser.email}`} onClose={() => (reviewQueueUser = null)} wide>
    <EmailReviewQueue
      reviewApi={adminEmailReviewApi.forUser(reviewQueueUser.id)}
      backfillApi={adminEmailBackfillApi.forUser(reviewQueueUser.id)}
      queryKey={['admin-email-review', reviewQueueUser.id]}
    />
  </Modal>
{/if}
